/**
 * Balance card.
 *
 * On first paint the card is rendered server-side by the template (see
 * `templates/frontend/review-for-points.php`). After an AJAX login or
 * register, however, the page is not reloaded — so we need a way to
 * inject the card dynamically. This module owns both code paths:
 *
 *   - {@link update}: refresh the values inside an existing
 *     `[data-marc-balance]` block (used after submit success bumps the
 *     pending count, and after refetch on auth events).
 *   - {@link render}: build the markup from scratch and insert it
 *     between `.marc__header` and the next sibling. Idempotent — a
 *     second call falls through to {@link update}.
 */

import { config } from '../config.js';

const t = (key, fallback) => config?.i18n?.[key] ?? fallback;

const formatNumber = (value) => {
    const n = Number.isFinite(+value) ? +value : 0;
    return n.toLocaleString();
};

/**
 * Resolve the displayable values out of an AJAX `accountSummary`
 * payload. Centralised so the first-paint markup builder and the
 * in-place updater always render the same values for the same input —
 * before v3.6.8 they diverged on edge cases (eg. `balance_formatted`
 * being null / undefined / containing `<sup>`).
 *
 * Return shape mirrors the spans the markup renders: `amount`,
 * `unit`, `earned`, `pending`, `approved`, plus a `muted` flag for
 * the "balance unavailable" tone.
 */
const resolveDisplay = (data) => {
    const has = Boolean(data?.has_balance);
    const summary = data?.summary ?? {};
    return {
        muted:    !has,
        amount:   has ? (data.balance_formatted ?? '0') : '—',
        unit:     has ? (data.point_type_label ?? '') : t('balanceUnavailable', ''),
        earned:   formatNumber(summary.lifetime_earned ?? 0),
        pending:  formatNumber(summary.pending ?? 0),
        approved: formatNumber(summary.approved ?? 0),
    };
};

/**
 * Build the markup. Stays semantically identical to the server-rendered
 * card so the same SCSS rules apply.
 *
 * @param {object} data Same shape as resolveDisplay's input.
 */
const buildCardHtml = (data) => {
    const v = resolveDisplay(data);
    return `
<section class="marc__balance" data-marc-balance>
    <div class="marc__balance-main">
        <span class="marc__balance-label">${escapeHtml(t('balanceLabel', 'Your balance'))}</span>
        <strong class="marc__balance-amount${v.muted ? ' marc__balance-amount--muted' : ''}">${escapeHtml(v.amount)}</strong>
        <span class="marc__balance-unit">${escapeHtml(v.unit)}</span>
    </div>
    <ul class="marc__balance-stats" aria-label="${escapeHtml(t('balanceSummaryAria', 'Review activity summary'))}">
        <li class="marc__balance-stat">
            <span class="marc__balance-stat-value">${escapeHtml(v.earned)}</span>
            <span class="marc__balance-stat-label">${escapeHtml(t('balanceEarnedReviews', 'Earned via reviews'))}</span>
        </li>
        <li class="marc__balance-stat">
            <span class="marc__balance-stat-value">${escapeHtml(v.pending)}</span>
            <span class="marc__balance-stat-label">${escapeHtml(t('balanceAwaitingReview', 'Awaiting review'))}</span>
        </li>
        <li class="marc__balance-stat">
            <span class="marc__balance-stat-value">${escapeHtml(v.approved)}</span>
            <span class="marc__balance-stat-label">${escapeHtml(t('balanceApproved', 'Approved submissions'))}</span>
        </li>
    </ul>
</section>`.trim();
};

/**
 * Tiny helper — every data-driven string we drop into innerHTML goes
 * through this. Keeps us safe from a hostile point-type label or a
 * formatted balance that contains literal HTML markup (myCred
 * `mycred_format_creds()` can include `<sup>` tags for decimals).
 */
const escapeHtml = (raw) => String(raw ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Update the values inside an existing card. Returns the section
 * element, or `null` when the card isn't on the page yet.
 *
 * Uses `textContent` (never `innerHTML`) for two reasons:
 *   1. Defense in depth against any hostile myCred label / format
 *      string that gets past our server-side validation.
 *   2. Visual parity with the first-paint path — `buildCardHtml()`
 *      escapes via `escapeHtml`, which strips the same set of HTML
 *      characters `textContent` does. So a raw `<sup>` tag in the
 *      formatted balance would render as the literal text `<sup>`
 *      either way; before v3.6.8 the two paths used different rules
 *      and the displayed string would shift after a refresh.
 */
const update = (root, data) => {
    const card = root.querySelector('[data-marc-balance]');
    if (!card) return null;

    const v = resolveDisplay(data);
    const amountEl = card.querySelector('.marc__balance-amount');
    const unitEl = card.querySelector('.marc__balance-unit');
    if (amountEl) {
        amountEl.textContent = v.amount;
        amountEl.classList.toggle('marc__balance-amount--muted', v.muted);
    }
    if (unitEl) {
        unitEl.textContent = v.unit;
    }

    const stats = card.querySelectorAll('.marc__balance-stat .marc__balance-stat-value');
    if (stats[0]) stats[0].textContent = v.earned;
    if (stats[1]) stats[1].textContent = v.pending;
    if (stats[2]) stats[2].textContent = v.approved;

    return card;
};

/**
 * Insert the card after `.marc__header`. If a card is already there
 * we delegate to {@link update} so callers can use this as a one-shot
 * "ensure the card is on the page and current" call.
 */
const render = (root, data) => {
    const existing = update(root, data);
    if (existing) return existing;

    const header = root.querySelector('.marc__header');
    if (!header) return null;

    const tmp = document.createElement('div');
    tmp.innerHTML = buildCardHtml(data);
    const card = tmp.firstElementChild;
    if (!card) return null;

    header.insertAdjacentElement('afterend', card);
    return card;
};

export const balanceCard = { render, update };
